import React, {useEffect, useState} from "react";
import {Box, Button, IconButton, Paper, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowTurnForwardIcon, Call02Icon, CallEnd01Icon, Mic01Icon, MicOff01Icon, ViewIcon
} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {useWebrtc} from "../../context/WebrtcContext";
import {RedirectCallDialog} from "./RedirectCallDialog";
import {LiveAvatar} from "../../pages/registry-project/dashboard/components/conversationUi";
import {formatTime} from "../../utils/functions";

const roundSx = (bg, color = "#fff") => ({
  width: 40, height: 40, borderRadius: "50%", bgcolor: bg, color, "&:hover": {bgcolor: bg, filter: "brightness(0.92)"},
});

const CallTimer = ({startedAt}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return <>{formatTime(Math.max(0, (now - startedAt) / 1000))}</>;
};

const TITLES = {ringing: "Incoming call", connecting: "Connecting…", active: "On call"};
// "Call customer" (see callCustomer in WebrtcProvider): the agent's leg is answered, then the customer is dialed
const PHASE_TITLES = {
  agent_ringing: "Connecting…", agent_answered: "Dialing…", customer_ringing: "Ringing…",
  customer_answered: "On call", ended: "Call ended", agent_failed: "Call failed",
};
const titleOf = (call) => (call.outbound && PHASE_TITLES[call.phase])
  || (call.group && call.state === "ringing" ? "Incoming group call" : TITLES[call.state]);

// The browser phone's current call (see WebrtcProvider): ringing with Answer / Reject, then Mute, Hang up and a
// timer. The call's conversation (X-Conversation-Id) opens in supervision, where the bot transcript is shown.
export const WebrtcCallPopup = () => {
  const {call, answer, reject, hangup, toggleMute} = useWebrtc();
  const navigate = useNavigate();
  const [redirectOpen, setRedirectOpen] = useState(false);
  useEffect(() => {
    if (!call) setRedirectOpen(false);
  }, [call]);
  if (!call) return null;
  // only a caller's call the agent answered; the agent's own "Call customer" calls cannot be redirected
  const canRedirect = call.state === "active" && call.conversationId && !call.outbound;

  const openConversation = () => {
    if (!call.projectId || !call.conversationId) return;
    const base = call.subProjectId
      ? `/registry-project/${call.projectId}/${call.subProjectId}`
      : `/registry-project/${call.projectId}`;
    navigate(`${base}/supervision?conversation=${encodeURIComponent(call.conversationId)}`);
  };

  return (
    <Paper elevation={0} role={"dialog"} aria-label={titleOf(call)} sx={{
      position: "fixed", top: 72, right: 16, zIndex: 1400, width: 320, maxWidth: "calc(100vw - 32px)", p: 2,
      borderRadius: "18px", border: "1px solid #ECECF3", bgcolor: "#fff", boxShadow: "0 16px 48px rgba(35, 38, 77, 0.18)",
    }}>
      <Stack direction={"row"} alignItems={"center"} gap={1.5}>
        <LiveAvatar size={44} iconSize={20}/>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8491F7", textTransform: "uppercase"}}>
            {titleOf(call)}
          </Typography>
          <Typography noWrap sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>{call.caller}</Typography>
          <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>
            {call.group ? `Group ${call.group.name || call.group.number || call.group.id} · ` : ""}
            {call.conversationId ? `Conversation #${call.conversationId}` : "Browser call"}
            {call.state === "active" && call.startedAt && (!call.outbound || call.phase === "customer_answered") ? <> · <CallTimer startedAt={call.startedAt}/></> : null}
          </Typography>
        </Box>
      </Stack>

      {call.conversationId && call.projectId && (
        <Button
          onClick={openConversation} fullWidth startIcon={<HugeiconsIcon icon={ViewIcon} size={16}/>}
          sx={{
            mt: 1.5, height: 34, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
            color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
          }}
        >
          Open conversation
        </Button>
      )}

      <Stack direction={"row"} justifyContent={"center"} gap={2} sx={{mt: 2}}>
        {call.state === "ringing" ? (
          <>
            <Tooltip title={"Reject"}>
              <IconButton onClick={reject} sx={roundSx("#E5484D")} aria-label={"Reject"}>
                <HugeiconsIcon icon={CallEnd01Icon} size={18}/>
              </IconButton>
            </Tooltip>
            <Tooltip title={"Answer"}>
              <IconButton onClick={answer} sx={roundSx("#1E8A55")} aria-label={"Answer"}>
                <HugeiconsIcon icon={Call02Icon} size={18}/>
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title={call.muted ? "Unmute" : "Mute"}>
              <IconButton
                onClick={toggleMute} aria-label={call.muted ? "Unmute" : "Mute"}
                sx={call.muted ? roundSx("#23264D") : roundSx("#F4F4FA", "#23264D")}
              >
                <HugeiconsIcon icon={call.muted ? MicOff01Icon : Mic01Icon} size={18}/>
              </IconButton>
            </Tooltip>
            {canRedirect && (
              <Tooltip title={"Redirect call"}>
                <IconButton onClick={() => setRedirectOpen(true)} aria-label={"Redirect call"} sx={roundSx("#F4F4FA", "#23264D")}>
                  <HugeiconsIcon icon={ArrowTurnForwardIcon} size={18}/>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={"Hang up"}>
              <IconButton onClick={hangup} sx={roundSx("#E5484D")} aria-label={"Hang up"}>
                <HugeiconsIcon icon={CallEnd01Icon} size={18}/>
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
      <RedirectCallDialog open={redirectOpen} onClose={() => setRedirectOpen(false)} call={call}/>
    </Paper>
  );
};
